import { t as globalT, t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  Button,
  Group,
  Select,
  Stack,
  TagsInput,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useInsertMutation } from "@supabase-cache-helpers/postgrest-react-query";
import { PostgrestError } from "@supabase/supabase-js";
import { ErrorAlert, LoadingSuspense } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, useCallback, useMemo } from "react";

import { api } from "@/api.ts";
import { ProjectSelect } from "@/components/ProjectSelect.tsx";
import { DatasetUriMultiSelect } from "@/components/clients/DatasetUriMultiSelect.tsx";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";
import { useZitadelApplications } from "@/hooks/useZitadelApplications.ts";

export const Route = createFileRoute("/admin/clients/create")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Create client`,
      },
    ],
  }),
});

const CreateForm: FC = () => {
  const { data: applications } = useZitadelApplications();
  const { data: existingClients } = useSuspenseQueryPostgrest(
    useMemo(() => api.from("client").select("id"), []),
  );

  const registeredClientIds = useMemo(
    () => new Set(existingClients.map((c) => String(c.id))),
    [existingClients],
  );

  const clientIdOptions = useMemo(
    () =>
      applications
        .filter((app) => !registeredClientIds.has(app.client_id))
        .map((app) => ({
          value: app.client_id,
          label: `${app.name} (${app.client_id})`,
        })),
    [applications, registeredClientIds],
  );

  const form = useForm({
    initialValues: {
      client_id: "",
      name: "",
      owned_by: "",
      redirect_uris: [] as Array<string>,
      client_datasets: [] as Array<string>,
      permitted_datasets: [] as Array<string>,
      project_id: null as number | null,
      client_dataset_project_id: null as number | null,
    },
    validate: {
      client_id: (value) => (value ? null : t`Client ID is required`),
      name: (value) => (value.trim().length > 0 ? null : t`Name is required`),
    },
  });

  const { mutateAsync: insert } = useInsertMutation(
    api.from("client"),
    ["id"],
    "id",
  );
  const navigate = Route.useNavigate();

  const handleSubmit = useCallback<Parameters<(typeof form)["onSubmit"]>[0]>(
    async (values) => {
      try {
        const result = await insert([
          {
            id: values.client_id as unknown as number,
            name: values.name,
            owned_by: values.owned_by || null,
            redirect_uris: values.redirect_uris,
            client_datasets: values.client_datasets,
            permitted_datasets: values.permitted_datasets,
            project_id: values.project_id,
            client_dataset_project_id: values.client_dataset_project_id,
          },
        ]);
        await navigate({
          to: "/admin/clients/$id",
          params: { id: String(result?.[0].id) },
        });
      } catch (e) {
        if (e instanceof PostgrestError) {
          if (e.code === "23505") {
            form.setFieldError(
              "client_id",
              t`A client with this ID has already been registered.`,
            );
          } else {
            form.setFieldError("__root", e.message);
          }
        } else if (e instanceof Error) {
          form.setFieldError("__root", e.message);
        }
      }
    },
    [form, insert, navigate],
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <h1>
          <Trans>Create new client</Trans>
        </h1>
        <Select
          label={t`Client ID`}
          description={t`The Zitadel OIDC application this client corresponds to`}
          placeholder={t`Select an application...`}
          withAsterisk
          searchable
          disabled={form.submitting}
          data={clientIdOptions}
          key={form.key("client_id")}
          {...form.getInputProps("client_id")}
        />
        <TextInput
          label={t`Name`}
          withAsterisk
          disabled={form.submitting}
          key={form.key("name")}
          {...form.getInputProps("name")}
        />
        <TextInput
          label={t`Owned by`}
          description={t`Email address of the user responsible for this client`}
          disabled={form.submitting}
          key={form.key("owned_by")}
          {...form.getInputProps("owned_by")}
        />
        <TagsInput
          label={t`Redirect URIs`}
          disabled={form.submitting}
          key={form.key("redirect_uris")}
          {...form.getInputProps("redirect_uris")}
        />
        <DatasetUriMultiSelect
          label={t`Client datasets`}
          description={t`Datasets this client has implicit access to, without specifying a project`}
          disabled={form.submitting}
          value={form.values.client_datasets}
          onChange={(value) => form.setFieldValue("client_datasets", value)}
        />
        <DatasetUriMultiSelect
          label={t`Permitted datasets`}
          description={t`Datasets this client has access to without project-level permission`}
          disabled={form.submitting}
          value={form.values.permitted_datasets}
          onChange={(value) => form.setFieldValue("permitted_datasets", value)}
        />
        <ProjectSelect
          label={t`Project`}
          description={t`The project this client belongs to, if any`}
          clearable
          disabled={form.submitting}
          value={form.values.project_id}
          onChange={(value) => form.setFieldValue("project_id", value)}
        />
        <ProjectSelect
          label={t`Client dataset project`}
          description={t`Project to use if no other project is specified and only datasets within "Client datasets" are accessed`}
          clearable
          disabled={form.submitting}
          value={form.values.client_dataset_project_id}
          onChange={(value) =>
            form.setFieldValue("client_dataset_project_id", value)
          }
        />
        <Group justify="flex-end" mt="md">
          <Button
            type="submit"
            loading={form.submitting}
            disabled={form.submitting}
          >
            <Trans>Create</Trans>
          </Button>
        </Group>
        {form.errors.__root && <ErrorAlert>{form.errors.__root}</ErrorAlert>}
      </Stack>
    </form>
  );
};

function RouteComponent() {
  return (
    <LoadingSuspense>
      <CreateForm />
    </LoadingSuspense>
  );
}
